
import apiConfig from '@/config/api.config'
import { formatItemQty } from '@/utils/formatUnit'
import OrderTimeline from '@/components/common/OrderTimeline.vue'
import OrderLocationMap from '@/components/common/OrderLocationMap.vue'
import PhoneCallButton from '@/components/common/PhoneCallButton.vue'
import { fetchPublicConfig } from '@/utils/publicConfig'
import { resolveOrderLocation } from '@/utils/orderLocation'
import ExportButton from '@/components/common/ExportButton.vue'
import { openPickupConfirm } from '@/utils/pickupConfirm'
// 对话原语（原生弹窗治理 Phase 1）：本文件 37 处原生弹窗（30 alert + 6 confirm + 1 prompt）全部迁到这里。
// 分类口径见 docs/NATIVE_DIALOG_AUDIT.md §3.1（先判 C，再判 D，再判 B，兜底 A）与本文件逐处注释。
import { showMessage, confirmAction, showForm } from '@/utils/dialog'
import {
  ROLES,
  SOURCE_STATUS,
  getDisplayStatus,
  getAvailableActions,
  canPerformAction,
  ADMIN_STATUS_OPTIONS,
  formatPackingInfo
} from '@/utils/orderStatusUnified'

export default {
  name: 'OrderManage',
  components: { OrderLocationMap, PhoneCallButton, OrderTimeline, ExportButton },
  data () {
    return {
      newOrderCount: 0, // 快照轮询发现的新订单数（点击提示条才刷新列表，不自动刷新）
      defaultImage: 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="60" height="60"%3E%3Crect fill="%23f0f0f0" width="60" height="60"/%3E%3C/svg%3E',
      orders: [],
      mergedOrders: [],
      filteredOrders: [],
      filterStatus: '', // 默认显示全部订单（原 'pending' 会把已验货/配送中等运作中订单过滤掉）
      filterFulfillmentMode: '',
      customerSearch: '',
      startDate: '',
      endDate: '',
      // 服务端分页：直连 GET /admin/orders 的 page/limit/pagination.total
      pagination: { page: 1, limit: 20, total: 0 },
      selectedOrder: null,
      // 公共配置（自提地址当前值）：resolver 的旧数据 fallback 依据
      publicCfg: null,
      detailTab: 'overview', // 详情 drawer 当前 Tab
      shipModalVisible: false,
      shippingGroup: null,
      // 状态广播机制 - 暂存待确认的变更
      pendingChanges: {}, // key: groupId, value: newStatus
      batchTargetStatus: '', // 批量改状态目标值（走 pendingChanges → 确认广播）
      loading: false,
      // 子订单商品展开/折叠状态
      collapsedOrders: {}, // key: orderId, value: boolean (true=折叠, false=展开)
      // 管理员操作面板
      adminActionOrder: null,
      adminActionModal: false,
      // 状态纠错
      correctTargetStatus: '',
      correctReason: '',
      // 数量调整
      adjustItem: null,
      adjustQuantity: 1,
      adjustReason: '',
      adjustModalVisible: false,
      childDetailOrder: null,
      childDetailVisible: false,
      adjustData: {},
      addModalVisible: false,
      addForm: { product_id: '', quantity: 1, reason: '' },
      replaceModalVisible: false,
      replaceTarget: null,
      replaceForm: { new_product_id: '', quantity: 1, reason: '' },
      // 商品搜索（新增/替换共用）
      addProductKeyword: '',
      addProductResults: [],
      addSelectedProduct: null,
      replaceProductKeyword: '',
      replaceProductResults: [],
      replaceSelectedProduct: null,
      // 展开的订单组ID数组（Vue 2 响应式）
      expandedGroups: [],
      // 勾选归档相关
      selectedOrders: [],
      selectAll: false,
      // 指派配送员相关
      assignModalVisible: false,
      assignGroup: null,
      assignees: [],
      selectedAssigneeId: null,
      // 管理员状态下拉选项（来自统一映射）
      adminStatusOptions: ADMIN_STATUS_OPTIONS,
      // 角色常量，供模板使用
      ROLES: ROLES,
      _refreshTimer: null
    }
  },
  computed: {
    // 订单地址统一 resolver：文字/地图/导航同一来源（pickup→快照/当前自提地址；配送→收货地址）
    selectedLoc () {
      return resolveOrderLocation(this.selectedOrder, this.publicCfg)
    },
    totalPages () {
      return Math.ceil(this.pagination.total / this.pagination.limit) || 1
    },
    exportGroups () {
      return [
        { label: '订单', templates: [
          { value: 'order-master', label: '订单主数据', desc: '履约订单（fo 为权威状态源）' },
          { value: 'order-items', label: '订单明细', desc: 'SKU × 订单明细行' },
          { value: 'order-events', label: '履约审计链', desc: '谁在什么时候做了什么' }
        ]}
      ]
    },
    totalItemsCount () {
      // 只统计待配货状态的商品
      return this.filteredOrders
        .filter(g => this.getEffectiveStatus(g) === 'picking' || g.status === 'picking')
        .reduce((sum, g) => sum + g.totalQuantity, 0)
    },
    pendingConfirmCount () {
      return this.filteredOrders.filter(g => this.getEffectiveStatus(g) === 'pending_confirm').length
    },
    totalAmount () {
      return this.filteredOrders
        .reduce((sum, g) => sum + parseFloat(g.totalAmount), 0)
        .toFixed(2)
    },
    pendingChangesCount () {
      return Object.keys(this.pendingChanges).length
    }
  },
  created () {
    // 从 route query 恢复筛选状态
    if (this.$route.query.status) {
      this.filterStatus = this.$route.query.status
    }
    // Assistant 订单卡入口：?open=fo.id → 列表加载后自动拉起现有详情抽屉（showOrderDetail 复用）
    if (this.$route.query.open) this._autoOpenOrderId = String(this.$route.query.open)
    // 自提地址当前值：旧数据无 snapshot 时 resolver 的 fallback 依据
    fetchPublicConfig().then(cfg => { this.publicCfg = cfg }).catch(() => {})
    this.fetchOrders()
  },
  mounted () {
    this._startPolling()
  },
  beforeDestroy () {
    this._stopPolling()
  },
  watch: {
    // filterStatus 变化时同步到 route query，保证刷新/返回保留
    filterStatus (val) {
      const query = { ...this.$route.query }
      if (val) {
        query.status = val
      } else {
        delete query.status
      }
      this.$router.replace({ query }).catch(() => {})
    },
    // Assistant 订单卡跳转（?open=fo.id）：已在本页时 query 变化也能拉起
    '$route.query.open' (v) {
      if (v) {
        this._autoOpenOrderId = String(v)
        this.maybeAutoOpenDetail()
      }
    }
  },
  methods: {
    formatItemQty,
    canPerformAction,

    // === 管理员操作面板 ===
    openAdminActions (order) {
      this.childDetailVisible = false
      this.adminActionOrder = order
      this.adminActionModal = true
      this.correctTargetStatus = ''
      this.correctReason = ''
      this.adjustReason = ''
    },
    getCorrectOptions (order) {
      const s = order._sourceStatus || order.status
      // delivering→inspected, delivered→delivering（shipped 已废弃，不再提供修正入口）
      const map = {
        'delivering': [{ value: 'inspected', label: '→ 退回待配送（误操作）' }],
        'delivered': [{ value: 'delivering', label: '→ 退回配送中（异常处理）' }]
      }
      return map[s] || []
    },
    // 迁移（原生弹窗治理 Phase 1）：confirm → confirmAction。
    // 归 **B** 而非 C：强制完成是可回退的强制态切换（审计 §3.1 判定边界——本弹窗内的
    // 「状态修正」就是回退入口），代价可逆，故用白底主色键，不用危险红。
    // 请求放进弹窗内执行（数据自持）：loading 期间键 disabled（防双击重复提交），
    // 失败留在弹窗里显示服务端原因、不静默关掉。
    async adminForceConfirm () {
      await confirmAction({
        parent: this,
        title: '强制完成订单',
        message: '确认强制完成此订单？',
        consequence: '履约单将直接进入已完成，跳过常规流程；操作有误可用「状态修正」退回。',
        okLabel: '强制完成',
        action: async () => {
          const token = localStorage.getItem('token')
          await this.axios.post(`/admin/fulfillment-orders/${this.adminActionOrder.id}/confirm`, {}, { headers: { Authorization: `Bearer ${token}` } })
          showMessage('强制确认成功', { type: 'positive' })
          this.adminActionModal = false
          this.followFilterStatus('confirmed')
          this.fetchOrders()
        }
      })
    },
    // 「确认自提」入口的共同前置条件（2026-09-25）：逐条对齐后端
    // POST /admin-mobile/orders/:id/confirm-pickup 的守卫顺序（admin-mobile.js:582-599）——
    // 角色白名单 → 自提模式 → 未完成/未取消 → 已验货 → operator 本人负责。
    // 前端只决定按钮显不显示（不满足即不渲染，不做 disabled 长期占位），判定口与拦截仍在后端。
    // 注：/admin 路由 requireAdmin 已保证 PC 端只可能是 admin/super_admin，operator 分支是对齐语义
    // 的防漂移兜底，不是新增权限。
    canConfirmPickup (order) {
      if (!order) return false
      const role = this.$store.getters.currentUserRole
      if (!['super_admin', 'admin', 'operator'].includes(role)) return false
      if (order.fulfillment_mode !== 'pickup') return false
      const s = order._sourceStatus || order.status
      if (s === 'confirmed' || s === 'cancelled') return false
      if (order.inspection_status !== 'completed') return false
      if (role === 'operator' && order.operator_id !== this.$store.getters.currentUserId) return false
      return true
    },
    // 自提单完成：等价标记送达（delivered_at 写入）+ 直达 confirmed，不等用户确认。
    // 两个入口（详情操作区 / 改单弹窗的更多操作）共用这一段：同一个弹窗、同一段成功后续刷新。
    // 2026-09-24：window.prompt/alert 换成 Quasar 弹窗（展示本单自提码供核对，失败留在弹窗内）
    confirmPickupFor (order, after) {
      openPickupConfirm(this, order, () => {
        this.adminActionModal = false
        this.followFilterStatus('confirmed')
        this.fetchOrders()
        if (typeof after === 'function') after()
      })
    },
    // 详情操作区入口：成功后按 fo id 重拉详情，抽屉里的状态/动态立刻反映终态（按钮随之消失）
    confirmPickupFromDetail () {
      const foId = this.selectedOrder && this.selectedOrder.id
      this.confirmPickupFor(this.selectedOrder, () => {
        if (foId && this.selectedOrder && this.selectedOrder.id === foId) this.showOrderDetail(this.selectedOrder)
      })
    },
    async adminCorrectStatus () {
      try {
        const token = localStorage.getItem('token')
        await this.axios.post(`/admin/fulfillment-orders/${this.adminActionOrder.id}/correct-status`, {
          target_status: this.correctTargetStatus, reason: this.correctReason
        }, { headers: { Authorization: `Bearer ${token}` } })
        // A 类（纯告知）：无返回值、执行流不依赖用户选择 → Notify，不弹窗
        showMessage('状态修正成功', { type: 'positive' })
        this.adminActionModal = false
        this.followFilterStatus(this.correctTargetStatus)
        this.fetchOrders()
      } catch (err) { showMessage('修正失败: ' + (err.response?.data?.message || ''), { type: 'negative' }) }
    },
    paymentText (o) {
      const method = { offline: '线下', online: '在线', erp: 'ERP', monthly: '月结', cash: '现金', wechat_offline: '微信线下' }[o.payment_method] || o.payment_method || ''
      const status = { not_required: '无需追踪', pending: '待支付', paid: '已支付', failed: '失败', refunded: '已退款' }[o.payment_status] || o.payment_status
      return method + ' · ' + status
    },
    async adminAdjustItems () {
      const order = this.adminActionOrder
      const items = (order.items || []).filter(i => !i.is_gift)
      const toAdjust = items.filter(i => this.adjustData[i.id] && parseInt(this.adjustData[i.id]) !== i.quantity && parseInt(this.adjustData[i.id]) >= 1)
      // A 类（校验提示）：需要用户补输入才能继续，不是「告知成功」→ warning 色，不弹窗
      if (toAdjust.length === 0) { showMessage('没有需要调整的商品', { type: 'warning' }); return }
      try {
        const token = localStorage.getItem('token')
        for (const item of toAdjust) {
          await this.axios.put(`/admin/orders/${order.id}/items/${item.id}`, {
            quantity: parseInt(this.adjustData[item.id]), reason: this.adjustReason
          }, { headers: { Authorization: `Bearer ${token}` } })
        }
        showMessage('调整成功', { type: 'positive' })
        Object.keys(this.adjustData).forEach(k => delete this.adjustData[k])
        this.adminActionModal = false
        this.fetchOrders()
      } catch (err) { showMessage('调整失败: ' + (err.response?.data?.message || ''), { type: 'negative' }) }
    },
    showChildDetail (order) {
      this.childDetailOrder = order
      this.childDetailVisible = true
    },
    canAdjustOrder (order) {
      const s = order._sourceStatus || order.status
      return ['pending_confirm', 'picked', 'inspected'].includes(s)
    },
    // 是否"合并单里的子单"：只有这种情况才可能出现"删到 0 件 → 子单不再参与履约"
    isMergedChildContext () {
      const src = (this.selectedOrder && this.selectedOrder.source_orders) || []
      return src.length >= 2 && !(this.adminActionOrder && this.adminActionOrder._isFoDetail)
    },
    // 迁移（原生弹窗治理 Phase 1）：原为「确认 + 原因输入」**两次串行原生弹窗**（迁移前 1105 行 / 1106 行）——
    // 第二步 prompt 被取消（或输入为空）时函数直接 return，**用户完全不知道商品为什么没删**
    // （审计 §5.2 点名的「全仓最典型 confirm+prompt 应合并成结构化弹窗」样例）。
    // 现合并为**一个**结构化表单（D 类唯一出口 showForm）：后果说明 + 必填原因一次填完、一次校验、一次提交。
    //
    // 危险点（审计 §3.3 C 类）：DELETE /admin/orders/:foId/items/:itemId —— 物理删除订单项、
    // 库存将恢复；删到「该子单最后一件商品」时该子单**不再参与履约、促销与配送**（原两分支后果文案原样保留，未丢失）。
    // C∩D：本处既是危险动作（C）又必须输入删除原因（D），所以走 showForm 的 danger 变体——
    // 危险语义由「低饱和红确认键 + 独立后果块 + 必填原因 + persistent:true」承载，
    // 不因为"多了一个输入框"就退回主色键（§五「红色不得滥用」的反面）。
    async adminDeleteItem (item) {
      // 保底口径在服务端（整组至少一个非赠品）；这里只把"会清空子单"这件事提前说清楚
      const childNonGift = (this.adminActionOrder.items || []).filter(i => !i.is_gift).length
      const foNonGift = (this.selectedOrder && this.selectedOrder.items ? this.selectedOrder.items : []).filter(i => !i.is_gift).length
      const willEmpty = this.isMergedChildContext && childNonGift <= 1 && foNonGift > 1
      const consequence = willEmpty
        ? '这是该子单最后一件商品，删除后该子单不再参与履约、促销与配送。'
        : '删除后不可恢复，库存将恢复。'
      await showForm({
        parent: this,
        title: '删除订单商品',
        message: '确认删除商品「' + item.product_name + '」？',
        danger: true, // C∩D：确认键走 --destructive 低饱和红，后果块独立显示
        consequence,
        okLabel: '删除',
        persistent: true, // 危险操作：点遮罩/ESC 不关，必须明确选取消或删除
        fields: [{
          key: 'reason',
          label: '删除原因',
          type: 'text',
          required: true,
          requiredMessage: '请填写删除原因',
          placeholder: '必填，将随操作记录'
        }],
        onSubmit: async (values) => {
          const token = localStorage.getItem('token')
          const res = await this.axios.delete(`/admin/orders/${this.adminActionOrder.id}/items/${item.id}`, { headers: { Authorization: `Bearer ${token}` }, data: { reason: values.reason } })
          // 清空子单的文案由服务端判定（data.child_emptied），客户端不猜
          const emptied = !!(res.data && res.data.data && res.data.data.child_emptied)
          showMessage(
            emptied ? '管理员已清空该子单商品，子单不再参与履约。' : '删除成功',
            { type: emptied ? 'warning' : 'positive' }
          )
          this.adminActionModal = false
          this.fetchOrders()
        }
      })
    },
    openReplaceModal (item) {
      this.replaceTarget = item
      this.replaceForm = { new_product_id: '', quantity: 1, reason: '' }
      this.replaceProductKeyword = ''
      this.replaceProductResults = []
      this.replaceSelectedProduct = null
      this.replaceModalVisible = true
    },
    // 通用商品搜索（新增/替换共用，消除重复）
    async searchProducts (keyword) {
      const kw = (keyword || '').trim()
      if (!kw) return []
      try {
        const token = localStorage.getItem('token')
        const res = await this.axios.get(`/admin/products?keyword=${encodeURIComponent(kw)}&limit=10`, { headers: { Authorization: `Bearer ${token}` } })
        if (res.data.code === 200) return res.data.data || []
      } catch (e) { console.error('商品搜索失败', e) }
      return []
    },
    onAddProductSearch () {
      clearTimeout(this._addSearchTimer)
      this._addSearchTimer = setTimeout(async () => {
        this.addProductResults = await this.searchProducts(this.addProductKeyword)
      }, 300)
    },
    pickAddProduct (p) {
      this.addSelectedProduct = p
      this.addForm.product_id = p.id
      this.addProductKeyword = p.name
      this.addProductResults = []
    },
    onReplaceProductSearch () {
      clearTimeout(this._replaceSearchTimer)
      this._replaceSearchTimer = setTimeout(async () => {
        this.replaceProductResults = await this.searchProducts(this.replaceProductKeyword)
      }, 300)
    },
    pickReplaceProduct (p) {
      this.replaceSelectedProduct = p
      this.replaceForm.new_product_id = p.id
      this.replaceProductKeyword = p.name
      this.replaceProductResults = []
    },
    async adminReplaceItem () {
      try {
        const token = localStorage.getItem('token')
        await this.axios.post(`/admin/orders/${this.adminActionOrder.id}/items/${this.replaceTarget.id}/replace`, {
          new_product_id: this.replaceForm.new_product_id, quantity: this.replaceForm.quantity, reason: this.replaceForm.reason
        }, { headers: { Authorization: `Bearer ${token}` } })
        showMessage('替换成功', { type: 'positive' })
        this.replaceModalVisible = false
        this.adminActionModal = false
        this.fetchOrders()
      } catch (err) { showMessage('替换失败: ' + (err.response?.data?.message || ''), { type: 'negative' }) }
    },
    openAddModal () {
      this.addForm = { product_id: '', quantity: 1, reason: '' }
      this.addProductKeyword = ''
      this.addProductResults = []
      this.addSelectedProduct = null
      this.addModalVisible = true
    },
    async adminAddItem () {
      try {
        const token = localStorage.getItem('token')
        await this.axios.post(`/admin/orders/${this.adminActionOrder.id}/items`, {
          product_id: this.addForm.product_id, quantity: this.addForm.quantity, reason: this.addForm.reason
        }, { headers: { Authorization: `Bearer ${token}` } })
        showMessage('添加成功', { type: 'positive' })
        this.addModalVisible = false
        this.adminActionModal = false
        this.fetchOrders()
      } catch (err) { showMessage('添加失败: ' + (err.response?.data?.message || ''), { type: 'negative' }) }
    },
    // 判断是否显示发货按钮 - 使用统一状态映射
    canShowShipButton (group) {
      // 发货按钮只在特定源状态下显示
      const effectiveStatus = this.getEffectiveStatus(group)
      return canPerformAction(effectiveStatus, 'ship', ROLES.ADMIN)
    },

    // 判断是否显示指派按钮 - 使用统一状态映射
    canShowAssignButton (group) {
      const effectiveStatus = this.getEffectiveStatus(group)
      return canPerformAction(effectiveStatus, 'assign', ROLES.ADMIN)
    },
    // 指派配送按钮标签（统一入口）
    getAssignLabel (group) {
      const name = group.assigned_courier_name || group.operator_name
      return name ? '改派' : '指派配送'
    },
    getAssignTitle (group) {
      const name = group.assigned_courier_name || group.operator_name
      return name ? `已指派：${name}（点击重新指派）` : '指派配送人员'
    },

    // 获取有效状态（考虑待确认变更）
    getEffectiveStatus (group) {
      return this.pendingChanges[group.id] || group.status
    },

    // 获取履约模式显示文本
    getFulfillmentModeText (mode) {
      const modeMap = {
        courier: '配送',
        pickup: '自提',
        express: '快递'
      }
      return modeMap[mode] || '配送'
    },

    // 检查是否有待确认变更
    hasPendingChange (group) {
      return group.id in this.pendingChanges
    },

    // 排队状态变更（不立即生效）
    queueStatusChange (group, newStatus) {
      if (newStatus === group.status) {
        this.$delete(this.pendingChanges, group.id)
      } else {
        this.$set(this.pendingChanges, group.id, newStatus)
      }
    },

    async confirmShip () {
      if (!this.shippingGroup) return
      const company = this.shippingGroup.express_company?.trim() || ''
      const tracking = this.shippingGroup.express_tracking_no?.trim() || ''
      const note = this.shippingGroup.delivery_note?.trim() || ''
      // A 类（校验提示）：缺输入、需要用户补齐才能发货 → warning，不弹窗
      if (!company && !tracking && !note) { showMessage('请至少填写一项运输信息（物流公司/单号/备注）', { type: 'warning' }); return }
      try {
        const token = localStorage.getItem('token')
        await this.axios.post(
          `/admin/fulfillment-orders/${this.shippingGroup.id}/ship`,
          { express_company: company, express_tracking_no: tracking, delivery_note: note },
          { headers: { Authorization: `Bearer ${token}` } }
        )
        showMessage('发货成功', { type: 'positive' })
        this.shipModalVisible = false
        this.shippingGroup = null
        this.followFilterStatus('delivering')
        this.fetchOrders()
      } catch (err) {
        console.error('发货失败:', err)
        showMessage('发货失败: ' + (err.response?.data?.message || '未知错误'), { type: 'negative' })
      }
    },

    // 清除所有待确认变更
    clearPendingChanges () {
      this.pendingChanges = {}
      this.$forceUpdate()
    },

    // 确认并广播状态变更
    batchQueueStatus () {
      // 批量状态修改：对所选 fo 逐个 queueStatusChange（进 pendingChanges，由"确认广播"统一提交）
      if (!this.batchTargetStatus) return
      this.selectedOrders.forEach(id => {
        const group = this.mergedOrders.find(g => g.id === id)
        if (group) this.queueStatusChange(group, this.batchTargetStatus)
      })
      this.batchTargetStatus = ''
    },
    openAdminActionsFromDetail () {
      // 从 drawer 操作 Tab 进入"纠错/强制完成"（fo 级；_isFoDetail 标记使 adminActionModal 隐藏改单部分，改单必须走 source order）
      this.adminActionOrder = Object.assign({}, this.selectedOrder, { _isFoDetail: true })
      this.adminActionModal = true
    },
    openChildDetailForEdit (sourceOrder) {
      // 改单基于 source order：打开来源订单详情 → 更多操作 → adminActionModal 改单（传 order.id）
      this.childDetailOrder = sourceOrder
      this.childDetailVisible = true
    },
    getNextStatusOptions (group) {
      // 当前状态允许的下一状态（限定白名单，非全枚举；选择后进 pendingChanges，由"确认广播"统一提交）
      const NEXT = {
        pending_confirm: [{ value: 'picked', label: '→ 已配货' }],
        picked: [{ value: 'inspected', label: '→ 已验货' }],
        // shipped 已废弃，inspected → delivering 直接进入配送
        inspected: [{ value: 'delivering', label: '→ 配送中' }],
        delivering: [{ value: 'delivered', label: '→ 已送达' }]
      }
      const cur = (group && (group._sourceStatus || group.status)) || ''
      return NEXT[cur] || []
    },
    // 迁移（原生弹窗治理 Phase 1）：confirm → confirmAction。
    // 归 **B**：跨流程改状态只写本地 pendingChanges（尚未提交），真正落库由「确认广播」二次提交并记日志，
    // 有明确回退路径（再次选择原状态即撤回），故不用危险红（审计 §3.1 判定边界）。
    // 注意：本方法原在同步函数内，引入 await 后必须改 async；调用方是模板 @change 的箭头函数，无需改。
    async confirmStatusChange (group, newStatus) {
      // 管理员完整状态权限：任意切换。正常下一状态直接进 pendingChanges；跨流程（异常）弹确认。
      const cur = (group && (group._sourceStatus || group.status)) || ''
      if (cur === newStatus) return
      const normal = this.getNextStatusOptions(group).map(o => o.value)
      const isAbnormal = normal.indexOf(newStatus) === -1
      if (isAbnormal) {
        const curLabel = (this.adminStatusOptions.find(o => o.value === cur) || {}).label || cur
        const newLabel = (this.adminStatusOptions.find(o => o.value === newStatus) || {}).label || newStatus
        const ok = await confirmAction({
          parent: this,
          title: '跨流程修改订单状态',
          message: `确认将订单状态从「${curLabel}」跨流程修改为「${newLabel}」？`,
          consequence: '本次选择只进入「待确认」，还需点击"确认广播"才会提交；提交会记录操作日志。',
          okLabel: '加入待确认'
        })
        if (!ok) return
      }
      this.queueStatusChange(group, newStatus)
    },
    async confirmBroadcast () {
      if (Object.keys(this.pendingChanges).length === 0) return

      const token = localStorage.getItem('token')
      const changes = Object.entries(this.pendingChanges)

      try {
        for (const [groupId, newStatus] of changes) {
          await this.axios.put(
            `/admin/fulfillment-orders/${groupId}/status`,
            { status: newStatus },
            { headers: { Authorization: `Bearer ${token}` } }
          )
          const group = this.mergedOrders.find(g => g.id === groupId)
          if (group) group.status = newStatus
        }

        this.pendingChanges = {}
        showMessage(`成功更新 ${changes.length} 个履约订单状态`, { type: 'positive' })
        this.fetchOrders()
      } catch (err) {
        console.error('状态变更失败:', err)
        showMessage('更新失败: ' + (err.response?.data?.message || '未知错误'), { type: 'negative' })
      }
    },

    // === 新订单检测（30s 轮询轻量快照，不自动刷新列表；页面不可见时跳过） ===
    _startPolling () {
      this._stopPolling()
      this._syncSnapshot()
      this._refreshTimer = setInterval(() => {
        if (document.hidden) return
        this.axios.get('/admin/orders-snapshot').then(res => {
          if (res.data.code !== 200) return
          const s = res.data.data || {}
          const base = this._snapshot
          // 基准 latest 为 null（空库起步）视作任何新单都更新；注意 JS 里 string>null 恒 false，必须显式判空
          const hasNewer = s.latest_created_at && (!base.latest_created_at || s.latest_created_at > base.latest_created_at)
          if (base && hasNewer && s.total > base.total) {
            this.newOrderCount = s.total - base.total
          }
        }).catch(() => {})
      }, 30000)
    },
    _stopPolling () {
      if (this._refreshTimer) {
        clearInterval(this._refreshTimer)
        this._refreshTimer = null
      }
    },
    // 快照基准 = 当前列表已反映的订单现实；仅在列表刷新后更新，轮询发现基准外的新单只提示不刷新
    _syncSnapshot () {
      this.axios.get('/admin/orders-snapshot').then(res => {
        if (res.data.code === 200) this._snapshot = res.data.data
      }).catch(() => {})
    },
    handleNewOrderClick () {
      this.fetchOrders() // 成功后 newOrderCount 清零 + 基准重置
    },

    // Assistant 订单卡跳转落点：?open=fo.id → 列表加载后自动拉起现有详情抽屉
    // （showOrderDetail 只需 id，自带详情拉取；一次性消费，找不到静默留在列表）
    maybeAutoOpenDetail () {
      const id = this._autoOpenOrderId
      if (!id) return
      const target = (this.orders || []).find(o => o && o.id === id)
      if (target) {
        this._autoOpenOrderId = null
        this.showOrderDetail(target)
      }
    },

    async fetchOrders () {
      // 过期保护：快速连续筛选/翻页时，旧响应晚到不许覆盖新响应（请求序号比对）
      const seq = this._fetchSeq = (this._fetchSeq || 0) + 1
      this.loading = true
      try {
        const token = localStorage.getItem('token')
        let url = apiConfig.apiList.getAdminOrders + '?'
        const params = []

        if (this.filterStatus) {
          params.push(`status=${this.filterStatus}`)
        }
        if (this.filterFulfillmentMode) {
          params.push(`fulfillment_mode=${this.filterFulfillmentMode}`)
        }
        if (this.startDate) {
          params.push(`startDate=${this.startDate}`)
        }
        if (this.endDate) {
          params.push(`endDate=${this.endDate}`)
        }
        if (this.customerSearch.trim()) {
          params.push(`keyword=${encodeURIComponent(this.customerSearch.trim())}`)
        }
        params.push(`page=${this.pagination.page}`)
        params.push(`limit=${this.pagination.limit}`)

        url += params.join('&')
        const res = await this.axios.get(url, {
          headers: { Authorization: `Bearer ${token}` }
        })
        if (seq !== this._fetchSeq) return
        if (res.data.code === 200) {
          this.orders = res.data.data || []
          this.pagination.total = (res.data.pagination && res.data.pagination.total) || 0
          // 空页守卫：末页数据被操作清空后回退一页（如批量归档删掉当页全部）
          if (!this.orders.length && this.pagination.page > 1) {
            this.pagination.page -= 1
            return this.fetchOrders()
          }
          this.processMergedOrders()
          this.applyFilter()
          this.maybeAutoOpenDetail()
          // 列表已反映现实 → 新订单提示清零、快照基准重置
          this.newOrderCount = 0
          this._syncSnapshot()
        }
      } catch (err) {
        console.error('获取订单失败:', err)
      } finally {
        this.loading = false
      }
    },

    // 筛选变化：回第 1 页再取（编辑/刷新等原路径不动 page，保持当前页）
    handleFilterChange () {
      this.pagination.page = 1
      this.fetchOrders()
    },

    goToPage (page) {
      if (page < 1 || page > this.totalPages || page === this.pagination.page) return
      this.pagination.page = page
      this.fetchOrders()
    },

    changePageSize (limit) {
      this.pagination.limit = parseInt(limit)
      this.pagination.page = 1
      this.fetchOrders()
    },

    processMergedOrders () {
      // fulfillment_order 已在后端聚合，这里只做 DTO→展示结构适配
      this.mergedOrders = (this.orders || [])
        .map(fo => this.adaptFulfillmentOrder(fo))
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    },

    // 把 FulfillmentOrderDTO 适配为页面展示结构（列表单位 = fulfillment_order）
    adaptFulfillmentOrder (fo) {
      const productMap = new Map()
      ;(fo.items || []).forEach(item => {
        const key = `${item.product_id || item.id}_${item.is_gift ? 'gift' : 'normal'}`
        if (productMap.has(key)) {
          productMap.get(key).totalQty += parseInt(item.quantity || 0)
        } else {
          productMap.set(key, {
            id: item.product_id || item.id,
            name: item.product_name || '未知商品',
            image: item.product_image || '',
            price: item.unit_price || item.price || 0,
            totalQty: parseInt(item.quantity || 0),
            unit: item.unit || null,
            pack_unit: item.pack_unit || null,
            pack_quantity: item.pack_quantity || 1,
            isGift: item.is_gift,
            giftType: item.gift_type
          })
        }
      })
      const productSummary = Array.from(productMap.values())
      const sources = fo.source_orders || []
      return {
        id: fo.fulfillment_order_id,
        display_order_no: fo.display_order_no,
        fulfillment_no: fo.fulfillment_no,
        isMerged: sources.length > 1,
        expanded: false,
        status: fo.status,
        display_text: fo.status_text,
        available_actions: fo.available_actions || [],
        fulfillment_mode: fo.fulfillment_mode || 'courier',
        customerId: fo.user_id,
        customerName: fo.user_nickname || fo.contact_name || '未知客户',
        customerPhone: fo.user_phone || '-',
        contactName: fo.contact_name,
        contactPhone: fo.contact_phone,
        contactAddress: fo.contact_address,
        pickup_address_snapshot: fo.pickup_address_snapshot != null ? String(fo.pickup_address_snapshot) : null,
        latitude: fo.latitude,
        longitude: fo.longitude,
        createdAt: fo.created_at,
        shortId: fo.display_order_no || fo.fulfillment_no,
        totalAmount: parseFloat(fo.total_amount || 0).toFixed(2),
        totalQuantity: parseInt(fo.total_quantity || 0),
        totalSkuCount: productSummary.length,
        productSummary,
        orders: sources, // 来源订单（展开用）
        source_order_count: sources.length,
        assigned_courier_id: fo.assigned_courier_id,
        assigned_courier_name: fo.assigned_courier_name,
        operator_id: fo.operator_id,
        operator_name: fo.operator_name,
        delivery_assignment_source: fo.delivery_assignment_source,
        inspection_status: fo.inspection_status,
        // 自提码（2026-09-25）：列表/详情 DTO 都带 pickup_code（adminQueryService），此前在本层
        // 适配时被丢掉，导致 PC 端「确认自提」弹窗取不到码 → 显示「无」且被后端 fail-closed 拒。
        // 与移动端口径一致：只做透传，历史长码原样。
        pickup_code: fo.pickup_code || null,
        originalAmount: parseFloat(fo.original_amount || 0).toFixed(2),
        discountAmount: parseFloat(fo.discount_amount || 0).toFixed(2),
        giftCount: fo.gift_count || 0,
        giftItems: fo.gift_items || [],
        couponInfo: fo.coupon_info || []
      }
    },

    createGroup (order, key) {
      return {
        id: key, // 使用稳定的 groupKey 作为 ID
        orders: [],
        isMerged: false,
        expanded: false,
        status: order.status,
        unified_status: order.unified_status,
        display_text: order.display_text,
        available_actions: order.available_actions || [],
        fulfillment_mode: order.fulfillment_mode || 'courier',
        customerId: order.user_id,
        customerName: order.user_nickname || order.user_name || '未知客户',
        customerPhone: order.user_phone || '-',
        wechatName: order.wechat_name || order.wechatName || '',
        wechatId: order.wechat_id || order.wechatId || '',
        contactName: order.contact_name || '',
        contactPhone: order.contact_phone || '',
        contactAddress: order.contact_address || '',
        pickup_address_snapshot: order.pickup_address_snapshot != null ? String(order.pickup_address_snapshot) : null,
        latitude: order.latitude,
        longitude: order.longitude,
        remark: order.remark || '',
        createdAt: order.created_at,
        shortId: '',
        totalAmount: 0,
        totalQuantity: 0,
        totalSkuCount: 0,
        productSummary: []
      }
    },

    aggregateOrderData (group) {
      group.isMerged = group.orders.length > 1
      group.shortId = group.isMerged
        ? `合并(${group.orders.length})`
        : group.orders[0].id.slice(0, 8)

      group.totalAmount = group.orders
        .reduce((sum, o) => sum + parseFloat(o.total_amount || 0), 0)
        .toFixed(2)
      group.totalQuantity = group.orders
        .reduce((sum, o) => sum + parseInt(o.total_quantity || 0), 0)

      const productMap = new Map()
      group.orders.forEach(order => {
        (order.items || []).forEach(item => {
          // 赠品和普通商品分开统计
          const key = `${item.product_id || item.id}_${item.is_gift ? 'gift' : 'normal'}`
          if (productMap.has(key)) {
            productMap.get(key).totalQty += parseInt(item.quantity || 0)
          } else {
            productMap.set(key, {
              id: item.product_id || item.id,
              name: item.product_name || '未知商品',
              image: item.product_image || '',
              price: item.unit_price || item.price || 0,
              totalQty: parseInt(item.quantity || 0),
              isGift: item.is_gift,
              giftType: item.gift_type
            })
          }
        })
      })

      group.productSummary = Array.from(productMap.values())
      group.totalSkuCount = group.productSummary.length
    },

    formatSingleOrder (order) {
      const items = order.items || []
      const productMap = new Map()

      items.forEach(item => {
        // 赠品和普通商品分开统计
        const key = `${item.product_id || item.id}_${item.is_gift ? 'gift' : 'normal'}`
        if (productMap.has(key)) {
          productMap.get(key).totalQty += parseInt(item.quantity || 0)
        } else {
          productMap.set(key, {
            id: item.product_id || item.id,
            name: item.product_name || '未知商品',
            image: item.product_image || '',
            price: item.unit_price || item.price || 0,
            unitType: item.unit_type === 'package' ? 'package' : 'min_unit',
            totalQty: parseInt(item.quantity || 0),
            isGift: item.is_gift,
            giftType: item.gift_type
          })
        }
      })

      return {
        id: order.id,
        shortId: order.order_no || order.id.slice(0, 8),
        orders: [order],
        isMerged: false,
        expanded: false,
        status: order.status,
        unified_status: order.unified_status,
        display_text: order.display_text,
        available_actions: order.available_actions || [],
        fulfillment_mode: order.fulfillment_mode || 'courier',
        customerName: order.user_nickname || order.user_name || '未知客户',
        customerPhone: order.user_phone || '-',
        wechatName: order.wechat_name || order.wechatName || '',
        wechatId: order.wechat_id || order.wechatId || '',
        contactName: order.contact_name || '',
        contactPhone: order.contact_phone || '',
        contactAddress: order.contact_address || '',
        pickup_address_snapshot: order.pickup_address_snapshot != null ? String(order.pickup_address_snapshot) : null,
        latitude: order.latitude,
        longitude: order.longitude,
        remark: order.remark || '',
        createdAt: order.created_at,
        totalAmount: parseFloat(order.total_amount || 0).toFixed(2),
        totalQuantity: parseInt(order.total_quantity || 0),
        totalSkuCount: items.length,
        productSummary: Array.from(productMap.values())
      }
    },

    getTimeSlot (timestamp) {
      const date = new Date(timestamp)
      const hours = date.getHours()
      const dateStr = date.toISOString().split('T')[0]

      if (this.mergeInterval === 24) {
        return dateStr
      } else if (this.mergeInterval === 12) {
        // 同一天的上下午分开合并，不同天也分开
        return `${dateStr}_${hours < 12 ? 'AM' : 'PM'}`
      } else {
        const slot = Math.floor(hours / this.mergeInterval)
        return `${dateStr}_${slot}`
      }
    },

    // 客户搜索过滤
    // 操作后跟随订单新状态，切换筛选 tab
    followFilterStatus (newStatus) {
      if (newStatus && newStatus !== this.filterStatus) {
        this.filterStatus = newStatus
        // watch 自动同步到 route query
      }
    },
    handleSearch () {
      // 搜索已服务端化（GET /admin/orders keyword），防抖 400ms 回第 1 页
      clearTimeout(this._searchTimer)
      this._searchTimer = setTimeout(() => {
        this.pagination.page = 1
        this.fetchOrders()
      }, 400)
    },

    applyFilter () {
      // 筛选/搜索均由服务端完成，这里只做当页数据直通（保留函数：多处调用 + computed 消费 filteredOrders）
      this.filteredOrders = this.mergedOrders
    },

    // 获取显示的商品（限制4个）
    getDisplayProducts (group) {
      return group.productSummary.slice(0, 5)
    },

    // 计算虚拟填充块数量（保持对齐）
    getPlaceholderCount (group) {
      const count = Math.min(group.productSummary.length, 5)
      return count < 5 ? 5 - count : 0
    },

    showShipModal (group) {
      this.shippingGroup = {
        ...group,
        ship_type: 'express',
        express_company: '',
        express_tracking_no: '',
        vehicle_info: '',
        delivery_note: '',
        packing_unit: '',
        packing_count: null
      }
      this.shipModalVisible = true
    },

    // PC端一键验货（针对 fulfillment_order）
    // 迁移（原生弹窗治理 Phase 1）：confirm → confirmAction。
    // 归 **B**：批量但属常规状态推进（验货通过是正向流程），可再改，非破坏（审计 §3.4）。
    // 请求放进弹窗内：loading 防双击；失败留在弹窗里显示原因。
    async quickInspect (group) {
      await confirmAction({
        parent: this,
        title: '一键验货',
        message: '确认一键验货？将自动标记所有商品为验货通过。',
        consequence: '履约单进入已验货并流转到配送环节。',
        okLabel: '一键验货',
        action: async () => {
          const token = localStorage.getItem('token')
          const res = await this.axios.post(
            `/admin/fulfillment-orders/${group.id}/inspect`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          )
          if (!res.data || res.data.code !== 200) {
            throw new Error((res.data && res.data.message) || '验货失败')
          }
          showMessage('验货完成', { type: 'positive' })
          this.followFilterStatus('inspected')
          this.fetchOrders()
        }
      })
    },

    // 跳转到验货页面（移动端）
    goToInspection (group) {
      // 使用当前主机地址跳转到移动端验货页面
      const baseUrl = window.location.origin
      window.open(`${baseUrl}/#/order/${group.id}/inspection`, '_blank')
    },

    // 统一指派配送弹窗（配送员 + 业务员合并）
    async showUnifiedAssignModal (group) {
      this.assignGroup = group
      this.selectedAssigneeId = group.assigned_courier_id || group.operator_id || null
      this.assignModalVisible = true
      await this.fetchAssignees()
    },

    // 获取可配送人员列表（courier + operator 合并）
    async fetchAssignees () {
      try {
        const res = await this.axios.get('/admin/couriers', {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        })
        console.log('[FetchCouriers] Response:', res.data)
        if (res.data.code === 200) {
          this.assignees = (res.data.data || []).filter(u => u.role === 'courier' || u.role === 'operator')
        }
      } catch (err) {
        console.error('获取配送人员列表失败:', err)
        this.assignees = []
      }
    },

    // 确认统一指派
    async confirmUnifiedAssign () {
      if (!this.selectedAssigneeId || !this.assignGroup) return
      const person = this.assignees.find(p => p.id === this.selectedAssigneeId)
      if (!person) return
      const token = localStorage.getItem('token')
      const headers = { Authorization: `Bearer ${token}` }
      const foId = this.assignGroup.id
      const isOperator = person.role === 'operator'
      const url = isOperator
        ? `/admin/fulfillment-orders/${foId}/assign-operator`
        : `/admin/fulfillment-orders/${foId}/assign`
      const body = isOperator ? { operator_id: person.id } : { courier_id: person.id }
      console.log('[改派]', { foId, person: person.nickname, role: person.role, url, body })
      try {
        const res = isOperator
          ? await this.axios.put(url, body, { headers })
          : await this.axios.post(url, body, { headers })
        console.log('[改派] 响应:', res.data)
        if (res.data.code === 200) {
          showMessage('指派成功', { type: 'positive' })
          this.assignModalVisible = false
          this.assignGroup = null
          this.selectedAssigneeId = null
          this.fetchOrders()
        } else {
          showMessage('指派失败: ' + (res.data.message || '未知错误'), { type: 'negative' })
        }
      } catch (err) {
        const msg = err.response?.data?.message || err.message || '未知错误'
        console.error('[改派] 失败:', err.response?.status, msg, err.response?.data)
        showMessage('指派失败: ' + msg, { type: 'negative' })
      }
    },

    // [deprecated] 旧方法保留兼容，后续删除
    async showAssignModal (group) { this.showUnifiedAssignModal(group) },
    async confirmAssignCourier () { this.confirmUnifiedAssign() },
    async showAssignOpModal (group) { this.showUnifiedAssignModal(group) },
    async confirmAssignOperator () { this.confirmUnifiedAssign() },

    splitOrder (group) {
      this.$set(group, 'isMerged', false)
      this.$set(group, 'expanded', true)
    },

    async showOrderDetail (group) {
      try {
        const token = localStorage.getItem('token')
        const res = await this.axios.get(
          `${apiConfig.apiList.getAdminOrders}/${group.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        )
        if (res.data.code === 200) {
          const fo = res.data.data.fulfillment_order || {}
          const adapted = this.adaptFulfillmentOrder(fo)
          adapted.timeline = res.data.data.timeline || []
          adapted.fulfillment_events = res.data.data.fulfillment_events || []
          adapted.source_orders = fo.source_orders || []
          adapted.expandedSource = false // 来源订单默认折叠
          this.selectedOrder = adapted
        }
      } catch (err) {
        console.error('加载详情失败:', err)
        this.selectedOrder = group
      }
    },

    formatTime (timestamp) {
      const date = new Date(timestamp)
      return date.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })
    },

    formatDate (timestamp) {
      if (!timestamp) return '-'
      const date = new Date(timestamp)
      const pad = n => String(n).padStart(2, '0')
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
    },

    formatDateTime (timestamp) {
      const date = new Date(timestamp)
      return date.toLocaleString('zh-CN', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
    },
    modeLabel (mode) {
      const m = { courier: '配送', express: '快递', pickup: '自提' }
      return m[mode] || mode
    },

    // 获取子订单显示的商品列表
    getDisplayItems (items, orderId) {
      if (!items || items.length === 0) return []

      const isCollapsed = this.isCollapsed(orderId)
      const maxShow = 5
      const result = []

      if (items.length <= 3) {
        // 少于等于3个，全部显示，剩余用虚块填充
        items.forEach(item => result.push(item))
        const virtualCount = 3 - items.length
        for (let i = 0; i < virtualCount; i++) {
          result.push({ isVirtual: true, id: `virtual-${orderId}-${i}` })
        }
      } else if (items.length <= 5) {
        // 4-5个，显示全部
        items.forEach(item => result.push(item))
      } else {
        // 超过5个
        if (isCollapsed) {
          // 折叠状态：显示3个 + 更多按钮
          for (let i = 0; i < 3; i++) {
            result.push(items[i])
          }
          result.push({ isMore: true, id: `more-${orderId}` })
        } else {
          // 展开状态：显示全部 + 收起按钮
          items.forEach(item => result.push(item))
        }
      }

      return result
    },

    // 检查订单是否折叠
    isCollapsed (orderId) {
      return this.collapsedOrders[orderId] !== false
    },

    // 切换展开/折叠状态
    toggleExpand (orderId) {
      this.$set(this.collapsedOrders, orderId, !this.isCollapsed(orderId))
    },

    clearDateFilter () {
      this.startDate = ''
      this.endDate = ''
      this.handleFilterChange()
    },

    exportCSV () {
      // A 类（校验提示）：没有数据可导出，无需用户决定 → warning，不弹窗
      if (this.filteredOrders.length === 0) {
        showMessage('没有可导出的订单！', { type: 'warning' })
        return
      }

      let csvContent = '\uFEFF' // BOM for UTF-8
      // 扩展表头，包含更完整的订单信息
      csvContent += '订单ID,客户姓名,联系电话,微信号,收货人,收货电话,收货地址,订单状态,履约模式,总件数,总金额,下单时间,备注,订单商品\n'

      this.filteredOrders.forEach(group => {
        // 从group.orders获取子订单
        group.orders.forEach(order => {
          let itemsStr = ''
          if (order.items && order.items.length) {
            itemsStr = order.items.map(i => `${i.product_name}(x${i.quantity}${i.unit_type === 'package' ? '件' : '个'})`).join(' | ')
          }
          const safeString = (str) => {
            if (str === null || str === undefined) return ''
            return `"${String(str).replace(/"/g, '""')}"`
          }
          const row = [
            safeString(order.id),
            safeString(group.customerName),
            safeString(group.customerPhone),
            safeString(group.wechatName || group.wechatId),
            safeString(group.contactName),
            safeString(group.contactPhone),
            safeString(group.contactAddress),
            safeString(order.display_text || order.status),
            safeString(this.getFulfillmentModeText(order.fulfillment_mode)),
            safeString(order.total_quantity || 0),
            safeString(order.total_amount || 0),
            safeString(this.formatDate(order.created_at)),
            safeString(group.remark),
            safeString(itemsStr)
          ]
          csvContent += row.join(',') + '\n'
        })
      })

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute('download', `订单导出_${new Date().getTime()}.csv`)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },

    // 跳转到归档页面
    goToArchive () {
      this.$router.push('/admin/orders/archive')
    },

    // 自动归档（7天前已完成/已取消的订单）
    // 迁移（原生弹窗治理 Phase 1）：confirm → confirmAction。
    // 归 **B**：归档可逆——归档列表页有「恢复」入口（审计 §3.1 判定边界：可逆的归档 → B）。
    // 请求放进弹窗内：loading 防双击；失败留在弹窗里显示原因。
    async autoArchive () {
      await confirmAction({
        parent: this,
        title: '自动归档订单',
        message: '将自动归档7天前已完成或已取消的订单，确定继续吗？',
        consequence: '被归档的订单会移出当前列表，可在「归档列表」中恢复。',
        okLabel: '开始归档',
        action: async () => {
          const res = await this.axios.post('/admin/orders/auto-archive', { days: 7 })
          // 原实现只处理 200 分支（非 200 静默无反馈）；迁移后失败也给出可见原因，不再静默
          if (!res.data || res.data.code !== 200) {
            throw new Error((res.data && res.data.message) || '自动归档失败')
          }
          showMessage(res.data.message || '自动归档完成', { type: 'positive' })
          this.fetchOrders()
        }
      })
    },

    // 全选/取消全选
    toggleSelectAll () {
      // 只选择已完成和已取消的订单（可归档的）
      const archivable = this.filteredOrders
        .filter(g => g.status === 'confirmed' || g.status === 'completed' || g.status === 'cancelled')
      if (!this.selectAll && archivable.length > 0) {
        this.selectedOrders = archivable.map(g => g.id)
        this.selectAll = true
      } else {
        this.selectedOrders = []
        this.selectAll = false
      }
    },

    // 更新全选状态
    updateSelectAll () {
      const archivable = this.filteredOrders
        .filter(g => g.status === 'confirmed' || g.status === 'completed' || g.status === 'cancelled')
      this.selectAll = archivable.length > 0 && archivable.every(g => this.selectedOrders.includes(g.id))
    },

    // 清除选择
    clearSelection () {
      this.selectedOrders = []
      this.selectAll = false
    },

    // 批量归档（selectedOrders 存的即 fo.id——checkbox :value="group.id"，列表单位 = 履约单，
    // 与后端 /admin/orders/archive 入参同命名空间；勿再展开 source_orders 的子单 orders.id）
    // 迁移（原生弹窗治理 Phase 1）：confirm → confirmAction。
    // 归 **B**：归档可逆（归档列表页可恢复），故不用危险红（审计 §3.1 / §3.4）。
    // 请求放进弹窗内：loading 防双击；失败留在弹窗里显示原因。
    async batchArchive () {
      // A 类（校验提示）：未选订单 → warning，不弹窗
      if (this.selectedOrders.length === 0) {
        showMessage('请先选择要归档的订单', { type: 'warning' })
        return
      }

      await confirmAction({
        parent: this,
        title: '批量归档订单',
        message: `确定要归档选中的 ${this.selectedOrders.length} 个订单吗？`,
        consequence: '只有已完成或已取消的订单会被归档；归档后可在「归档列表」中恢复。',
        okLabel: '归档',
        action: async () => {
          const res = await this.axios.post('/admin/orders/archive', { orderIds: this.selectedOrders })
          // 原实现只处理 200 分支（非 200 静默无反馈）；迁移后失败也给出可见原因，不再静默
          if (!res.data || res.data.code !== 200) {
            throw new Error((res.data && res.data.message) || '批量归档失败')
          }
          showMessage(res.data.message || '归档完成', { type: 'positive' })
          this.clearSelection()
          this.fetchOrders()
        }
      })
    }
  }
}
